import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  VStack, HStack, Text, Badge, BadgeText, Spinner, Pressable, Icon, ScrollView, Button, ButtonText,
} from '@gluestack-ui/themed';
import { ChevronDown, ChevronRight } from 'lucide-react-native';
import {
  useGetSlotBookings,
  getGetSlotBookingsQueryKey,
  useSetBookingExamAdmission,
} from '../../../common/api/endpoints/appointments/appointments';
import {
  useGetExamSessionStatus,
  getGetExamSessionStatusQueryKey,
  useOpenExamSession,
  useCloseExamSession,
} from '../../../common/api/endpoints/exam-sessions/exam-sessions';
import { useApiErrorReporter } from '../../../common/utils/apiErrorReporter';
import { categorizeBookings } from '../utils/slotUtils';
import { TIME_CONSTANTS } from '../constants/slotConstants';
import { useEventCalendarData } from '../hooks/useEventCalendarData';
import EmailStudentsMenu from './EmailStudentsMenu';
import PreflightCheckInModal from './PreflightCheckInModal';

/**
 * SlotRegistrationsSection Component - Displays slot bookings with collapsible cancelled section
 */
const SlotRegistrationsSection = ({ slot, onActiveBookingsChange, scrollable = true }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { reportApiError } = useApiErrorReporter();
  const [showCancelled, setShowCancelled] = React.useState(false);
  const { taskMap, moduleMap } = useEventCalendarData();
  const isProctoredExam = slot.eventKindId === 'proctored_exam';
  const examStatusQueryKey = React.useMemo(
    () => getGetExamSessionStatusQueryKey(slot.slotId),
    [slot.slotId],
  );
  const slotBookingsQueryKey = React.useMemo(
    () => getGetSlotBookingsQueryKey(slot.slotId),
    [slot.slotId],
  );
  const [isPreflightOpen, setIsPreflightOpen] = React.useState(false);

  // Fetch slot bookings with polling
  const { data: bookingsData, isPending: bookingsLoading } = useGetSlotBookings(slot.slotId, {
    query: {
      enabled: !!slot.slotId,
      refetchInterval: TIME_CONSTANTS.LECTURER_BOOKING_POLL_INTERVAL,
    },
  });

  const bookings = bookingsData?.data?.data || [];
  const { active: activeBookings, cancelled: cancelledBookings } = categorizeBookings(bookings);
  const admissionMutation = useSetBookingExamAdmission();
  const openExamMutation = useOpenExamSession({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: examStatusQueryKey });
      },
      onError: (err) => {
        reportApiError(err, t('lecturer.eventCalendar.sidebar.openQuizErrorTitle', 'Open Quiz'));
      },
    },
  });
  const closeExamMutation = useCloseExamSession({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: examStatusQueryKey });
      },
      onError: (err) => {
        reportApiError(err, t('lecturer.eventCalendar.sidebar.closeQuizErrorTitle', 'Close Quiz'));
      },
    },
  });
  const { data: examSessionData } = useGetExamSessionStatus(slot.slotId, {
    query: {
      enabled: isProctoredExam && !!slot.slotId,
      refetchInterval: TIME_CONSTANTS.LECTURER_BOOKING_POLL_INTERVAL,
    },
  });
  const examSession = examSessionData?.data?.data || null;
  const examOpen = !!examSession?.openedAt && !examSession?.closedAt;
  const examClosed = !!examSession?.closedAt;
  const examActionPending = openExamMutation.isPending || closeExamMutation.isPending;
  const preflightPending = examActionPending || admissionMutation.isPending;

  // Group active bookings by task to save vertical space
  const activeBookingsByTask = React.useMemo(
    () => activeBookings.reduce((acc, b) => {
      const key = b.taskId || 'unassigned';
      if (!acc[key]) acc[key] = [];
      acc[key].push(b);
      return acc;
    }, {}),
    [activeBookings],
  );

  const getGroupTitles = (taskId, sampleBooking) => {
    const task = taskMap[taskId];
    const moduleTitle = (sampleBooking?.moduleId
      && (moduleMap[sampleBooking.moduleId] || sampleBooking.moduleId))
      || task?.moduleTitle
      || '';
    const taskTitle = task?.title || taskId;
    const taskVersion = sampleBooking?.taskVersion;
    return { moduleTitle, taskTitle, taskVersion };
  };

  // Notify parent of active bookings count changes
  React.useEffect(() => {
    if (onActiveBookingsChange) {
      onActiveBookingsChange(activeBookings.length);
    }
  }, [activeBookings.length, onActiveBookingsChange]);

  const defaultSelectedBookingIds = React.useMemo(
    () => activeBookings
      .filter((booking) => booking.examAdmission?.admitted)
      .map((booking) => booking.bookingId),
    [activeBookings],
  );

  const openPreflightModal = React.useCallback(() => {
    setIsPreflightOpen(true);
  }, []);

  const closePreflightModal = React.useCallback(() => {
    setIsPreflightOpen(false);
  }, []);

  const handleConfirmPreflight = React.useCallback(async (selectedBookingIds) => {
    try {
      const selectedSet = new Set(selectedBookingIds);
      const changedBookings = activeBookings.filter(
        (booking) => !!booking.examAdmission?.admitted !== selectedSet.has(booking.bookingId),
      );

      await Promise.all(changedBookings.map((booking) => admissionMutation.mutateAsync({
        slotId: slot.slotId,
        bookingId: booking.bookingId,
        data: { admitted: selectedSet.has(booking.bookingId) },
      })));

      await openExamMutation.mutateAsync({ slotId: slot.slotId, data: {} });
      await queryClient.invalidateQueries({ queryKey: slotBookingsQueryKey });
      setIsPreflightOpen(false);
    } catch (err) {
      reportApiError(err, t('lecturer.eventCalendar.sidebar.openQuizErrorTitle', 'Open Quiz'));
    }
  }, [
    activeBookings,
    admissionMutation,
    openExamMutation,
    queryClient,
    reportApiError,
    slot.slotId,
    slotBookingsQueryKey,
    t,
  ]);

  return (
    <VStack flex={1} justifyContent="flex-start" style={{ minHeight: 0 }}>
      {!!isProctoredExam && (
        <VStack space="xs" mb="$2">
          <HStack alignItems="center" justifyContent="space-between">
            <Text size="2xs" color="$textSubtle">
              {t('lecturer.eventCalendar.sidebar.examSession', 'EXAM SESSION')}
            </Text>
            <Badge
              size="xs"
              variant="solid"
              action={examClosed ? 'error' : examOpen ? 'success' : 'muted'}
            >
              <BadgeText size="xs">
                {examClosed
                  ? t('lecturer.eventCalendar.sidebar.examClosed', 'Closed')
                  : examOpen
                    ? t('lecturer.eventCalendar.sidebar.examOpen', 'Open')
                    : t('lecturer.eventCalendar.sidebar.examLocked', 'Locked')}
              </BadgeText>
            </Badge>
          </HStack>
          <Button
            size="xs"
            onPress={() => (examOpen
              ? closeExamMutation.mutate({ slotId: slot.slotId })
              : openPreflightModal())}
            isDisabled={examActionPending || (!examOpen && activeBookings.length === 0)}
          >
            <ButtonText>
              {examActionPending
                ? (examOpen
                  ? t('lecturer.eventCalendar.sidebar.closingQuiz', 'Closing Quiz...')
                  : t('lecturer.eventCalendar.sidebar.openingQuiz', 'Opening Quiz...'))
                : examOpen
                  ? t('lecturer.eventCalendar.sidebar.closeQuiz', 'Close Quiz')
                  : examClosed
                    ? t('lecturer.eventCalendar.sidebar.reopenQuiz', 'Reopen Quiz')
                    : t('lecturer.eventCalendar.sidebar.openQuiz', 'Open Quiz')}
            </ButtonText>
          </Button>
          {!examOpen && activeBookings.length === 0 && (
            <Text size="2xs" color="$textSubtle">
              {t(
                'lecturer.eventCalendar.sidebar.preflightNoRegistrations',
                'No active registrations for this slot.',
              )}
            </Text>
          )}
        </VStack>
      )}

      <HStack alignItems="center" justifyContent="space-between" mb="$1">
        <Text size="2xs" color="$textSubtle">
          {t('lecturer.eventCalendar.sidebar.registrations').toUpperCase()}
        </Text>
        <HStack alignItems="center" space="sm">
          <Text size="2xs" color="$textSubtle">
            {`${activeBookings.length}/${slot.capacity || 1}`}
          </Text>
          <EmailStudentsMenu slot={slot} activeBookings={activeBookings} activeBookingsByTask={activeBookingsByTask} />
        </HStack>
      </HStack>

      {bookingsLoading ? (
        <Spinner size="small" />
      ) : bookings.length > 0 ? (
        <VStack flex={1} style={{ minHeight: 0 }}>
          {/* Active bookings grouped by task, scrollable to avoid overlapping buttons and keep footer visible */}
          {scrollable ? (
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 8 }} showsVerticalScrollIndicator>
              {Object.entries(activeBookingsByTask).map(([taskId, items]) => {
                const titles = getGroupTitles(taskId, items[0]);
                return (
                  <VStack key={taskId} space="xs" py="$1">
                    {/* Group header with module / task */}
                    <HStack space="xs" alignItems="center" mb="$1">
                      <Badge variant="outline" action="muted" w="100%">
                        <BadgeText size="2xs">
                          {titles.moduleTitle}
                          {titles.moduleTitle && titles.taskTitle ? ' / ' : ''}
                          {titles.taskTitle}
                          {titles.taskVersion ? ` · v${titles.taskVersion}` : ''}
                        </BadgeText>
                      </Badge>
                    </HStack>
                    {/* Students in this task */}
                    {items.map((booking) => (
                      <HStack key={booking.bookingId} alignItems="center" space="sm" py="$1">
                        <Text size="sm" color="$textBody" flex={1}>
                          {booking.studentName}
                        </Text>
                        <Badge size="sm" variant="solid" action={booking.status === 'confirmed' ? 'success' : 'muted'}>
                          <BadgeText size="xs">{t(`lecturer.eventCalendar.sidebar.status.${booking.status}`)}</BadgeText>
                        </Badge>
                      </HStack>
                    ))}
                  </VStack>
                );
              })}
              {/* Cancelled bookings - collapsible, remains inside scroll area */}
              {cancelledBookings.length > 0 && (
                <VStack space="xs" mt="$2">
                  <Pressable onPress={() => setShowCancelled(!showCancelled)} px="$0">
                    <HStack alignItems="center" space="sm">
                      <Icon as={showCancelled ? ChevronDown : ChevronRight} size="xs" color="$textHint" />
                      <Text size="xs" color="$textSubtle" flex={1}>
                        {`${cancelledBookings.length} cancellation${cancelledBookings.length > 1 ? 's' : ''}`}
                      </Text>
                    </HStack>
                  </Pressable>

                  {showCancelled && (
                    <VStack space="xs" pl="$5">
                      {cancelledBookings.map((booking) => (
                        <VStack key={booking.bookingId} space="xs" py="$1" px="$0">
                          <HStack alignItems="center" space="sm">
                            <Text size="xs" color="$textHint" flex={1}>
                              {booking.studentName}
                            </Text>
                            <Badge size="xs" variant="outline" action="error">
                              <BadgeText size="xs" color="$red500">
                                {t(`lecturer.eventCalendar.sidebar.status.${booking.status}`)}
                              </BadgeText>
                            </Badge>
                          </HStack>
                        </VStack>
                      ))}
                    </VStack>
                  )}
                </VStack>
              )}
            </ScrollView>
          ) : (
            <VStack space="xs" pb="$2">
              {Object.entries(activeBookingsByTask).map(([taskId, items]) => {
                const titles = getGroupTitles(taskId, items[0]);
                return (
                  <VStack key={taskId} space="xs" py="$1">
                    {/* Group header with module / task */}
                    <HStack space="xs" alignItems="center" mb="$1">
                      <Badge variant="outline" action="muted" w="100%">
                        <BadgeText size="2xs">
                          {titles.moduleTitle}
                          {titles.moduleTitle && titles.taskTitle ? ' / ' : ''}
                          {titles.taskTitle}
                          {titles.taskVersion ? ` · v${titles.taskVersion}` : ''}
                        </BadgeText>
                      </Badge>
                    </HStack>
                    {/* Students in this task */}
                    {items.map((booking) => (
                      <HStack key={booking.bookingId} alignItems="center" space="sm" py="$1">
                        <Text size="sm" color="$textBody" flex={1}>
                          {booking.studentName}
                        </Text>
                        <Badge size="sm" variant="solid" action={booking.status === 'confirmed' ? 'success' : 'muted'}>
                          <BadgeText size="xs">{t(`lecturer.eventCalendar.sidebar.status.${booking.status}`)}</BadgeText>
                        </Badge>
                      </HStack>
                    ))}
                  </VStack>
                );
              })}
              {/* Cancelled bookings - collapsible */}
              {cancelledBookings.length > 0 && (
                <VStack space="xs" mt="$2">
                  <Pressable onPress={() => setShowCancelled(!showCancelled)} px="$0">
                    <HStack alignItems="center" space="sm">
                      <Icon as={showCancelled ? ChevronDown : ChevronRight} size="xs" color="$textHint" />
                      <Text size="xs" color="$textSubtle" flex={1}>
                        {`${cancelledBookings.length} cancellation${cancelledBookings.length > 1 ? 's' : ''}`}
                      </Text>
                    </HStack>
                  </Pressable>

                  {showCancelled && (
                    <VStack space="xs" pl="$5">
                      {cancelledBookings.map((booking) => (
                        <VStack key={booking.bookingId} space="xs" py="$1" px="$0">
                          <HStack alignItems="center" space="sm">
                            <Text size="xs" color="$textHint" flex={1}>
                              {booking.studentName}
                            </Text>
                            <Badge size="xs" variant="outline" action="error">
                              <BadgeText size="xs" color="$red500">
                                {t(`lecturer.eventCalendar.sidebar.status.${booking.status}`)}
                              </BadgeText>
                            </Badge>
                          </HStack>
                        </VStack>
                      ))}
                    </VStack>
                  )}
                </VStack>
              )}
            </VStack>
          )}
        </VStack>
      ) : (
        <Text size="sm" color="$textSubtle" py="$4">
          {t('lecturer.eventCalendar.sidebar.noRegistrations')}
        </Text>
      )}
      <PreflightCheckInModal
        isOpen={isPreflightOpen}
        onClose={closePreflightModal}
        bookings={activeBookings}
        defaultSelectedBookingIds={defaultSelectedBookingIds}
        onConfirm={handleConfirmPreflight}
        isSubmitting={preflightPending}
        isReopen={examClosed}
      />
    </VStack>
  );
};

export default SlotRegistrationsSection;
